import { createContext, useContext } from 'react';

import { AndFilterInput, FacetFilterInput } from '../../types.generated';

// Dashboard-level filter selections keyed by the search field they map to
// (e.g. 'domains', 'owners', 'dataProducts'). An empty array means "no filter for that field".
export type DashboardFilterSelections = Record<string, string[]>;

// Converts the raw selections into FacetFilterInput clauses, dropping any field with no selection.
// e.g. { domains: ['urn:..'], owners: [] } -> [{ field: 'domains', values: ['urn:..'] }]
export function buildDashboardFilters(selections: DashboardFilterSelections): FacetFilterInput[] {
    return Object.entries(selections)
        .filter(([, urns]) => urns.length > 0)
        .map(([field, values]) => ({ field, values }));
}

// Merges the dashboard filter clauses into a widget's existing orFilters using AND semantics.
// - No dashboard filters    -> the widget's base orFilters are returned unchanged.
// - No base orFilters       -> a single AND block holding the dashboard filters.
// - Existing base orFilters  -> the dashboard filters are appended to every AND block.
export function mergeDashboardFilters(
    filters: FacetFilterInput[],
    baseOrFilters?: AndFilterInput[],
): AndFilterInput[] | undefined {
    if (!filters.length) {
        return baseOrFilters;
    }
    if (!baseOrFilters?.length) {
        return [{ and: filters }];
    }
    return baseOrFilters.map((block) => ({ and: [...(block.and ?? []), ...filters] }));
}

// Returns the selected urns for the given field (empty when nothing is selected).
export function getFieldValues(filters: FacetFilterInput[], field: string): string[] {
    return filters.find((f) => f.field === field)?.values ?? [];
}

// Carries the applied dashboard filter clauses to every widget so they can scope their queries.
const DashboardFiltersContext = createContext<FacetFilterInput[]>([]);

export const DashboardFiltersProvider = DashboardFiltersContext.Provider;

export const useDashboardFilters = () => useContext(DashboardFiltersContext);
